/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 * Uses Supabase's resetPasswordForEmail with proper redirect URL.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { buildResetPasswordUrl } from '@/core/auth/redirect-utils';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

type PageState = 'form' | 'sent' | 'error';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState<PageState>('form');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const trimmedEmail = email.trim().toLowerCase();

      if (!trimmedEmail) {
        setError('Please enter your email address.');
        setLoading(false);
        return;
      }

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: buildResetPasswordUrl(),
      });

      if (resetError) {
        console.error('[ForgotPassword] Reset error:', resetError);
        // Don't reveal if email exists or not
        // Always show success message for security
      }

      // Always show success to prevent email enumeration
      setPageState('sent');
    } catch (err) {
      console.error('[ForgotPassword] Unexpected error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (pageState === 'sent') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="relative z-10 w-full max-w-md">
          <div className="glass-strong rounded-2xl p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Check Your Email</h1>
            <p className="text-muted-foreground mb-6">
              If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              The link will expire in 1 hour. Check your spam folder if you don't see the email.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => {
                  setEmail('');
                  setPageState('form');
                }}
                variant="outline"
                className="w-full"
              >
                Send Another Link
              </Button>
              <Link to="/login">
                <Button className="w-full">
                  Return to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>

        <div className="glass-strong rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold">Reset Your Password</h1>
            <p className="text-muted-foreground mt-2">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="doctor@hospital.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-muted/50 pl-10"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Remember your password?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Need help? Email{' '}
            <a className="text-primary hover:underline" href="mailto:support@radiologylive.ai">
              support@radiologylive.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
